import InputField from "@components/FormControls/CatalystTextField";

import { yupResolver } from "@hookform/resolvers/yup";
import { RegisterSchema, registerSchema } from "@validators/auth";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";

import toast from "react-hot-toast";
import { register as signUp } from "@contexts/AuthContext/functions/register";
import { Fieldset, FieldGroup, Label } from "@components/Catalyst/fieldset";
import { Text } from "@components/Catalyst/text";
import { Button } from "@components/Catalyst/button";
import { Checkbox, CheckboxField } from "@components/Catalyst/checkbox";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid";
import { PreRegisterUser } from "@services/GACApi4/user/preRegister";
import { BrandNewslettersType } from "@services/GACApi4/schemas/newsletterSubscription";
import { BRAND_NEWSLETTER_OPTIONS } from "@constants/newsletterBrandOptions";

const SmallRegisterForm = () => {
  const [agreement, setAgreement] = useState(false);
  const [brandSelections, setBrandSelections] = useState<BrandNewslettersType[]>([]);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<RegisterSchema>({
    resolver: yupResolver(registerSchema),
    mode: "onChange",
    defaultValues: { newsletterSubscription: false },
  });

  const goldenExchangeChecked = watch("newsletterSubscription");

  const toggleBrand = (value: BrandNewslettersType, checked: boolean) => {
    setBrandSelections((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
  };

  const allBrandsSelected = brandSelections.length === BRAND_NEWSLETTER_OPTIONS.length;

  const toggleAllBrands = () => {
    setBrandSelections(allBrandsSelected ? [] : BRAND_NEWSLETTER_OPTIONS.map((o) => o.value));
  };

  const onSubmit = async (data: RegisterSchema) => {
    toast.loading("Submitting your registration...", { id: "auth-status" });

    await PreRegisterUser({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      newsletterSubscriptionDetail: {
        firstName: data.firstName,
        lastName: data.lastName,
        brandNewsletters: brandSelections,
        newsletterSubscriptions: data.newsletterSubscription ? ["golden-updates"] : [],
      },
    });
    await signUp(data);
    toast.dismiss("auth-status");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Fieldset>
        <FieldGroup>
          <div className="flex flex-col md:grid md:grid-cols-2 md:place-items-end md:gap-4">
            <InputField
              className="w-full"
              required
              label="Name"
              register={register}
              name="firstName"
              error={errors.firstName}
              placeholder="First"
            />
            <InputField className="w-full" required placeholder="Last" register={register} name="lastName" error={errors.lastName} />
          </div>

          <div className="flex flex-col md:grid md:grid-cols-2 md:place-items-end md:gap-4">
            <InputField className="w-full" required label="Email" register={register} name="email" error={errors.email} />
            <InputField
              className="w-full"
              required
              register={register}
              name="confirmEmail"
              error={errors.confirmEmail}
              placeholder="Confirm email"
            />
          </div>

          <div className="flex flex-col">
            <div className="flex flex-col md:grid md:grid-cols-2 md:place-items-end md:gap-4">
              <InputField
                className="w-full"
                type="password"
                required
                label="Password"
                register={register}
                name="password"
                error={errors.password}
                autocomplete="new-password"
              />
              <InputField
                className="w-full"
                required
                type="password"
                register={register}
                name="passwordConfirmation"
                error={errors.passwordConfirmation}
                placeholder="Confirm password"
                autocomplete="new-password"
              />
            </div>
            <div className="text-sm text-gray-500">
              Password must be at least 8 characters in length and include a combination of upper and lowercase letters, numbers, and
              symbols
            </div>
          </div>

          <div>
            <CheckboxField>
              <Controller
                name="newsletterSubscription"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) setBrandSelections([]);
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              <Label> Join the Golden Exchange</Label>
            </CheckboxField>

            <p className="max-w-prose text-sm text-gray-500">
              Occasional emails with updates, artist input opportunities, and behind-the-scenes insights from Golden Artist Colors. <br />
              Sent monthly-ish. No spam, ever.
            </p>

            {goldenExchangeChecked && (
              <div className="ml-2 mt-3 flex flex-col gap-2 border-l-2 border-gray-200 pl-4">
                <p className="text-xs font-medium text-gray-600">Also receive brand-specific news:</p>
                <CheckboxField>
                  <Checkbox checked={allBrandsSelected} onChange={toggleAllBrands} />
                  <Label className="text-sm">Select all brands</Label>
                </CheckboxField>
                {BRAND_NEWSLETTER_OPTIONS.map((opt) => (
                  <CheckboxField key={opt.value}>
                    <Checkbox checked={brandSelections.includes(opt.value)} onChange={(checked) => toggleBrand(opt.value, checked)} />
                    <Label className="text-sm">{opt.label}</Label>
                  </CheckboxField>
                ))}
              </div>
            )}
          </div>

          <div>
            <CheckboxField>
              <Checkbox name="agree_to_terms" onChange={() => setAgreement(!agreement)} />
              <Label>I have reviewed and understand the Privacy Policy</Label>
            </CheckboxField>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              <div className="flex items-center text-sm text-cyan-700 underline">
                Privacy Policy
                <ArrowTopRightOnSquareIcon className="inline h-4 w-4 stroke-cyan-700" />
              </div>
            </a>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting || !isValid || !agreement}>
              Submit
            </Button>

            {!isValid && <Text className="content-center">Please ensure all fields are filled and errors are fixed.</Text>}
          </div>
        </FieldGroup>
      </Fieldset>
    </form>
  );
};

export default SmallRegisterForm;
